// src/pages/ResetPassword.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE_URL from '../utils/apiBase';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [redirectIn, setRedirectIn] = useState<number | null>(null);

  // After a successful reset, count down + redirect to /login so the user
  // doesn't have to navigate themselves. Cancelable if they manually leave.
  useEffect(() => {
    if (redirectIn === null) return;
    if (redirectIn <= 0) {
      navigate('/login');
      return;
    }
    const id = setTimeout(() => setRedirectIn(redirectIn - 1), 1000);
    return () => clearTimeout(id);
  }, [redirectIn, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Reset token is missing.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Reset failed');
      }

      setMessage('Password updated. Redirecting to sign in…');
      setRedirectIn(3);
    } catch (err) {
      console.error(err);
      setError('Unable to reset password. Please request a new link.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="section container">
      <div style={{ maxWidth: '450px', margin: '0 auto', padding: '2rem' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Reset Password</h1>
        <p className="text-secondary" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Choose a new password for your account.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {error && (
            <div style={{ padding: '1rem', backgroundColor: '#fee2e2', borderRadius: '6px', color: '#dc2626' }}>
              {error}
            </div>
          )}
          {message && (
            <div style={{ padding: '1rem', backgroundColor: '#ecfdf5', borderRadius: '6px', color: '#065f46' }}>
              <p style={{ margin: 0 }}>{message}</p>
              {redirectIn !== null && redirectIn > 0 && (
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
                  Redirecting in {redirectIn}…{' '}
                  <a href="/login" style={{ color: '#065f46', textDecoration: 'underline' }}>
                    Go now
                  </a>
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              placeholder="New password"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              placeholder="Confirm password"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem'
              }}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={isLoading} style={{ width: '100%' }}>
            {isLoading ? 'Updating...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
